import { Component, inject, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { MeetingService } from '../meeting.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { getNewMeetingFormObject } from './utils/new-meeting-form-object';
import { BehaviorSubject, catchError, forkJoin, of } from 'rxjs';
import { MeetingRepository } from '@/store/meeting-state/meeting-state.repository';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { ProfileService } from '@/pages/profile/profile.service';
import { getAppMembersFormObject, getInvitationFormObject } from './utils/add-guest-form-object';
import { DialogComponent } from '@/components/dialog/dialog.component';
import { StatusComponent } from '@/components/shared/status/status.component';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { MeetingWorkflowBlocksService } from '../meeting-workflow-blocks.service';
import { MeetingInviteeDialogComponent } from '@/components/shared/meeting-invitee-dialog/meeting-invitee-dialog.component';
import { CommitteeService } from '@/pages/settings/committee/committee.service';
import { Tasks, User, WithMetadata } from '@/types';
import { Meeting, MeetingDto, PlannedDossier } from '@/types/meeting.types';
import { newMeetingSchema } from '@/validations/meeting/new-meeting.validation';

@Component({
  selector: 'sm-new-meeting',
  imports: [PageHeaderComponent, FormBuilderComponent, StatusComponent, BlockBuilderComponent, MeetingInviteeDialogComponent],
  templateUrl: './new-meeting.component.html',
  styleUrl: './new-meeting.component.scss',
})
export class NewMeetingComponent implements OnInit, OnDestroy {
  meetingService = inject(MeetingService);
  meetingRepository = inject(MeetingRepository);
  meetingWorkflowBlocksService = inject(MeetingWorkflowBlocksService);
  comitteeService = inject(CommitteeService);
  translate = inject(TranslateService);
  router = inject(Router);
  profileService = inject(ProfileService);
  messageService = inject(MessageService);

  breadcrumb = [
    {
      label: 'meeting.breadcrumb.meeting',
      routerLink: ['/meetings'],
    },
    {
      label: 'meeting.breadcrumb.new',
      routerLink: ['/meetings', 'new'],
    },
  ];

  @ViewChild('dialog') dialog!: DialogComponent;

  loading = of(false);

  loadingComs$ = new BehaviorSubject<boolean>(false);
  loadingMeeting$ = new BehaviorSubject<boolean>(false);
  isComDisabled$ = new BehaviorSubject<boolean>(false);

  plannedDossiers: PlannedDossier[] = [];

  formObject: DynamicForm = getNewMeetingFormObject({
    comOptions: [],
    store: this.meetingRepository,
    plannedDossiers: this.plannedDossiers,
    comMembers: [],
    isComLoading: this.loadingComs$,
  });

  membersOptions: User[] = [];

  membersFormObject: DynamicForm = getAppMembersFormObject({
    membersOptions: this.membersOptions,
    store: this.meetingRepository,
    meetingService: this.meetingService,
  });

  inviteeFormObject: DynamicForm = getInvitationFormObject({
    store: this.meetingRepository,
  });

  comOptions: SelectOption[] = [];

  controlBlocks: DynamicBlocks = this.meetingWorkflowBlocksService.getNewMeetingBlocks({
    loading: this.loadingMeeting$,
    save: () => this.addMeeting(),
  });

  ngOnInit() {
    this.meetingRepository.reset();
    forkJoin({
      comOptions: this.profileService.getUserComs(),
    }).subscribe(({ comOptions }) => {
      this.comOptions = comOptions.map(com => {
        return { name: com.nom, code: com.id };
      });
      if (this.comOptions.length === 1) {
        this.meetingRepository.set('comSelected', this.comOptions[0]);
        const comId = this.comOptions[0].code;
        this.meetingRepository.setNested('dto', 'comId', comId);
        this.isComDisabled$.next(true);
        if (comId) {
          this.profileService.getUsersNotInCom(+comId).subscribe(users => {
            this.membersOptions = users;
            this.membersFormObject = getAppMembersFormObject({
              membersOptions: this.membersOptions,
              store: this.meetingRepository,
              meetingService: this.meetingService,
            });
          });
          this.loadCommitteeTasks(+comId).subscribe({
            next: result => this.applyTasksToPlannedDossiers(result),
          });
        }
      }
      this.formObject = getNewMeetingFormObject({
        comOptions: this.comOptions,
        store: this.meetingRepository,
        createAction: () => this.dialog.toggleVisible(),
        comMembers: [],
        plannedDossiers: this.plannedDossiers,
        onComChange: (comId: number) => this.handleComChange(comId),
        isComLoading: this.loadingComs$,
        isComDisabled: this.isComDisabled$,
      });
    });
  }

  addMeeting() {
    this.loadingMeeting$.next(true);
    const meetingDto: MeetingDto = {
      ...this.meetingRepository.get('dto'),
      reunionPpms: this.meetingRepository.getNested('dto', 'reunionPpms') ?? [],
      reunionDacs: this.meetingRepository.getNested('dto', 'reunionDacs') ?? [],
      participants: this.meetingRepository.getNested('dto', 'participants') ?? [],
      reunionDemandes: this.meetingRepository.getNested('dto', 'reunionDemandes') ?? [],
      reunionReports: this.meetingRepository.getNested('dto', 'reunionReports') ?? [],

    };
    const result = newMeetingSchema.safeParse(meetingDto);
    if (result.success) {
      this.meetingService.create(meetingDto).subscribe({
        next: (response: WithMetadata<Meeting>) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'La réunion a été créée avec succès',
          });
          this.loadingMeeting$.next(false);
          this.router.navigate(['/meetings', 'edit', response.id]);
        },
        error: (err: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || 'Une erreur est survenue lors de la création de la réunion',
          });
          this.loadingMeeting$.next(false);
        },
      });
    } else {
      this.loadingMeeting$.next(false);
      this.meetingRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }

  private handleComChange(comId: number) {
    this.loadingComs$.next(true);
    this.profileService.getUsersNotInCom(comId).subscribe(users => {
      this.membersOptions = users;
      this.membersFormObject = getAppMembersFormObject({
        membersOptions: this.membersOptions,
        store: this.meetingRepository,
        meetingService: this.meetingService,
      });
      this.meetingRepository.setNested('dto', 'participants', []);
      this.meetingRepository.setNested('dto', 'reunionPpms', []);
      this.meetingRepository.setNested('dto', 'reunionDacs', []);
      this.meetingRepository.setNested('dto', 'reunionDemandes', []);
      this.meetingRepository.setNested('dto', 'reunionReports', []);

      this.meetingRepository.set('plannedDossiers', []);
      this.plannedDossiers = [];
      this.loadingComs$.next(false);
    });
    this.loadCommitteeTasks(comId).subscribe({
      next: result => this.applyTasksToPlannedDossiers(result),
      complete: () => this.loadingComs$.next(false),
    });
  }

  private getTasksIntoPlannedDossiers(tasks: Tasks, type: 'PPM' | 'DAC' | 'ADDITIF' | 'REPORT') {
    tasks.tasks.forEach(task => {
      this.meetingRepository.set('plannedDossiers', [
        ...this.meetingRepository.get<PlannedDossier[]>('plannedDossiers'),
        {
          id: task.id,
          version: task.version,
          processInstanceId: task.processInstanceId,
          taskId: task.taskId,
          type,
          ref: task.ref,
          ac: {
            nom: task.nomAc,
          },
          checkPlanning: false,
        },
      ]);
    });
    this.plannedDossiers = this.meetingRepository.get('plannedDossiers');
  }

  private loadCommitteeTasks(comId: number) {
    return forkJoin({
      ppmTasks: this.meetingService.getComTasksPpms(+comId).pipe(
        catchError(error => {
          console.error('Error fetching tasks for the committee', error);
          return of(null);
        })
      ),
      dacTasks: this.meetingService.getComTasksDacs(+comId).pipe(
        catchError(error => {
          console.error('Error fetching tasks for the committee', error);
          return of(null);
        })
      ),
      additiveTasks: this.meetingService.getComTasksAdditives(+comId).pipe(
        catchError(error => {
          console.error('Error fetching tasks for the committee', error);
          return of(null);
        })
      ),
      reportTasks: this.meetingService.getComTasksReports(+comId).pipe(
        catchError(error => {
          console.error('Error fetching tasks for the committee', error);
          return of(null);
        })
      ),
    });
  }

  private applyTasksToPlannedDossiers(result: { ppmTasks: Tasks | null; dacTasks: Tasks | null; additiveTasks: Tasks | null; reportTasks:Tasks | null }) {
    this.meetingRepository.set('plannedDossiers', []);
    this.plannedDossiers = [];
    if (result.ppmTasks) this.getTasksIntoPlannedDossiers(result.ppmTasks, 'PPM');
    if (result.dacTasks) this.getTasksIntoPlannedDossiers(result.dacTasks, 'DAC');
    if (result.additiveTasks) this.getTasksIntoPlannedDossiers(result.additiveTasks, 'ADDITIF');
    if (result.reportTasks) this.getTasksIntoPlannedDossiers(result.reportTasks, 'REPORT');

    this.formObject = getNewMeetingFormObject({
      comOptions: this.comOptions,
      store: this.meetingRepository,
      createAction: () => this.dialog.toggleVisible(),
      comMembers: [],
      plannedDossiers: this.plannedDossiers,
      onComChange: (comId: number) => this.handleComChange(comId),
      isComLoading: this.loadingComs$,
      isComDisabled: this.isComDisabled$,
    });
  }

  ngOnDestroy() {
    this.meetingRepository.reset();
  }
}
